import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { clearAnonSessionId } from "@/lib/anonSession";

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  school?: string;
  field_of_study?: string;
  role: "admin" | "manager" | "user";
  ui_language: "fr" | "en";
  plan: "free" | "premium" | "school" | "vip";
  is_active: boolean;
  email_verified: boolean;
  custom_product_pitch?: string | null;
  tos_accepted_at?: string | null;
}

// Helper to get full name
export const getFullName = (user: User | null) => 
  user ? `${user.first_name} ${user.last_name}`.trim() : "";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  updateTokens: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      _hasHydrated: false,
      setAuth: (user, accessToken, refreshToken) => {
        // The guest→account claim runs server-side DURING the auth request (the
        // X-Anon-Session header was already serialized before this resolves), so
        // by the time we're authenticated the anon session id is consumed and
        // inert. Clear it centrally here — one clean lifecycle (minted at the
        // guest analysis → dropped at auth) across every path: Google, email/
        // password register/login, and the magic-link completion.
        clearAnonSessionId();
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
      },
      setUser: (user) => set({ user }),
      updateTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: "velyq-auth",
      storage: createJSONStorage(() => localStorage),
      // zustand v5 + localStorage rehydrates SYNCHRONOUSLY during create(),
      // i.e. before React's first client render. If `_hasHydrated` flips here
      // (or leaks in from a persisted blob — older builds serialized it as
      // `true`), the first client render shows auth-gated UI that the server
      // HTML omitted → guaranteed hydration mismatch on every SSR page view,
      // forcing React 19 to tear down and re-render the whole Suspense
      // boundary (and crashing with insertBefore NotFoundError if anything
      // external mutated the DOM meanwhile). The flag is flipped post-
      // hydration by <AuthHydrationMarker /> in the root layout instead.
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<AuthState>),
        _hasHydrated: false,
      }),
    }
  )
);

interface InterviewState {
  currentSessionId: string | null;
  currentQuestionIndex: number;
  setCurrentSession: (id: string) => void;
  setQuestionIndex: (index: number) => void;
  nextQuestion: () => void;
  reset: () => void;
}

export const useInterviewStore = create<InterviewState>((set) => ({
  currentSessionId: null,
  currentQuestionIndex: 0,
  setCurrentSession: (id) => set({ currentSessionId: id, currentQuestionIndex: 0 }),
  setQuestionIndex: (index) => set({ currentQuestionIndex: index }),
  nextQuestion: () =>
    set((state) => ({ currentQuestionIndex: state.currentQuestionIndex + 1 })),
  reset: () => set({ currentSessionId: null, currentQuestionIndex: 0 }),
}));
